/**
 * Zod runtime validation of a content pack (Deliverable d).
 *
 * Closed types make this cheap: because the `source` grammar and the expression DSL are both
 * closed and small, the schema can assert not just *shape* but *meaning* — every declared
 * expression must parse under `expr-eval`, and every `source` must parse into the closed
 * `static:`/`ref:` grammar. A pack that declares a rule the engine cannot honour fails loudly at
 * engine creation, with a precise path to the offending key — never silently dropped.
 *
 * Pure TypeScript, no Vue, no `src/data`. See `docs/ENGINE_SPEC.md`.
 */
import { z } from 'zod'
import { Parser } from 'expr-eval'
import {
  INITIATIVE_MODES,
  LIFECYCLE_EVENTS,
  RECOVERY_ACTORS,
  parseSource,
  type ContentPack,
} from './metamodel/types'

// Syntactic-only parser (same sandbox flags as the engine). Function/variable *names* are resolved
// at evaluate-time, so parsing here validates grammar without needing the pack's helpers registered.
const exprParser = new Parser({ allowMemberAccess: false })

/** A string that must parse as an `expr-eval` expression. `what` names the site for the error. */
const exprString = (what: string) =>
  z.string().refine(
    (v) => {
      try {
        exprParser.parse(v)
        return true
      } catch {
        return false
      }
    },
    { message: `not a valid expr-eval expression (${what})` },
  )

/** A `ChoicePoint.source` that must parse into the closed `static:`/`ref:` grammar. */
const sourceString = z.string().refine(
  (v) => {
    try {
      parseSource(v)
      return true
    } catch {
      return false
    }
  },
  { message: 'invalid choice source (expected "static:<set>" or "ref:<selection>.<field>")' },
)

const functionRule = z.object({
  params: z.array(z.string()),
  expr: exprString('functions.*.expr'),
})

const derivedRule = z.object({
  expr: exprString('derived.*.expr'),
  note: z.string().optional(),
})

const choicePoint = z.object({
  id: z.string().min(1),
  kind: z.enum(['single', 'multi']),
  source: sourceString,
  count: z.union([z.number(), z.string()]).optional(),
  eligible: exprString('choices[].eligible').optional(),
  label: z.string().optional(),
})

const flowStep = z.object({
  id: z.string().min(1),
  order: z.number(),
  title: z.string(),
  choices: z.array(choicePoint),
  valid: exprString('flow.steps[].valid'),
})

// Layer D §2.3 (schema 1.2.0): the declared turn-ordering axis. `mode` must be one of the closed
// `INITIATIVE_MODES` (unknown ⇒ loud-fail at `play.initiative.mode`); `expr` must parse under
// expr-eval. Both are checked at engine creation, like every other declared expression.
const initiativeAxis = z.object({
  mode: z.enum(INITIATIVE_MODES),
  expr: exprString('play.initiative.expr'),
})

// Layer D §2.4 (schema 1.3.0): the declared expiry vocabulary. `on` must be one of the closed
// `LIFECYCLE_EVENTS` (unknown ⇒ loud-fail at `play.conditions.expiries.N.on`); `count`, when present,
// must be a recognised counting mode. Mirrors `initiativeAxis` — same closed-set discipline.
const expiry = z.object({
  id: z.string().min(1),
  on: z.enum(LIFECYCLE_EVENTS),
  count: z.enum(['rounds', 'none']).optional(),
})
const conditionModel = z.object({ expiries: z.array(expiry) })

// Layer D §2.1 (schema 1.4.0): the declared dice model. `family` is closed (the discriminated union
// rejects an unrecognised family with a path-precise error at `play.dice.<name>`); each family carries
// only its own shape. Metadata describing how the host rolls — the engine never rolls (ADR-031).
const diceModel = z.discriminatedUnion('family', [
  z.object({ family: z.literal('d20'), sides: z.literal(20) }),
  z.object({
    family: z.literal('sum'),
    dice: z.array(z.object({ count: z.number(), sides: z.number() })),
  }),
  z.object({ family: z.literal('pool'), die: z.number(), success: z.number() }),
])

// Layer D §2.1 (schema 1.4.0): the declared resolution rule. Each `outcomes[].when` must parse under
// expr-eval; the `dice` ref and the `when` free-variable coverage are meaning-checked in crossCheckPlay
// (Zod asserts shape, the cross-check asserts the refs resolve) — same split as scopeContract.
const resolutionRule = z.object({
  dice: z.string().min(1),
  scope: z.array(z.string()),
  outcomes: z.array(
    z.object({ id: z.string().min(1), when: exprString('play.resolution.*.outcomes[].when') }),
  ),
})

// Layer D §2.2 (schema 1.5.0): the declared action economy. `kinds[].id` is an OPEN value (not a closed
// engine enum) — Zod asserts shape only; the `downgrades[].from`/`.to` refs are meaning-checked against the
// declared kinds in crossCheckPlay (same shape-vs-ref split as resolution's `dice`). ADR-030 §2.2.
const actionEconomy = z.object({
  kinds: z.array(z.object({ id: z.string().min(1), perTurn: z.number(), label: z.string() })),
  downgrades: z.array(z.object({ from: z.string().min(1), to: z.string().min(1) })).optional(),
})

// Layer D §2.5 (schema 1.5.0): a minimal progress clock. `advancesOn` is closed — the LIFECYCLE_EVENTS set
// plus the literal `'manual'` (unknown ⇒ loud-fail at `play.clocks.N.advancesOn`); the rest is shape. ADR-030 §2.5.
const clock = z.object({
  id: z.string().min(1),
  segments: z.number(),
  advancesOn: z.enum([...LIFECYCLE_EVENTS, 'manual']).optional(),
})

// Layer D §2.7 (ADR-086, schema 1.6.0): the declared recovery actions. Open values, closed types —
// the effect `kind` and the `actors` scope are CLOSED (`RECOVERY_EFFECTS` / `RECOVERY_ACTORS`; an
// unknown one fails loudly at `play.recovery.N.effects.M.kind` / `play.recovery.N.actors`), while the
// ids, names, usage keys and status ids inside are OPEN pack values. Every object is `.strict()` so a
// misspelt key (e.g. `skipIfStatuses`) fails at `play.recovery.N` instead of being stripped silently
// and read as "absent". The open values are checked by the host (`src/lib/recovery.ts`) and by the
// registry test over every shippable pack; the duplicate-id / at-most-one-per-kind rules are
// meaning-checks in crossCheckPlay below.
const recoveryEffect = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('end-encounter') }).strict(),
  z.object({ kind: z.literal('end-combat') }).strict(),
  z.object({ kind: z.literal('reset-usage'), keys: z.array(z.string().min(1)).min(1) }).strict(),
  z.object({ kind: z.literal('restore-hp') }).strict(),
  z.object({ kind: z.literal('restore-abilities') }).strict(),
  z
    .object({
      kind: z.literal('clear-effects'),
      statusIds: z.union([z.literal('all'), z.array(z.string().min(1)).min(1)]),
    })
    .strict(),
])

const recovery = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    hint: z.string().optional(),
    effects: z.array(recoveryEffect).min(1),
    actors: z.enum(RECOVERY_ACTORS).optional(),
    skipIfStatus: z.array(z.string().min(1)).min(1).optional(),
  })
  .strict()

const playModel = z.object({
  initiative: initiativeAxis.optional(),
  conditions: conditionModel.optional(),
  dice: z.record(z.string(), diceModel).optional(),
  resolution: z.record(z.string(), resolutionRule).optional(),
  actions: actionEconomy.optional(),
  clocks: z.array(clock).optional(),
  recovery: z.array(recovery).optional(),
})

const contentPackSchema = z.object({
  manifest: z.object({
    id: z.string().min(1),
    version: z.string().min(1),
    schemaVersion: z.string().min(1),
    license: z.string().min(1),
  }),
  vocabulary: z.object({
    abilities: z.array(z.string()),
    resources: z.array(z.string()),
    progression: z.object({ axis: z.string(), min: z.number(), max: z.number() }),
  }),
  functions: z.record(z.string(), functionRule),
  derived: z.record(z.string(), derivedRule),
  formulas: z.record(z.string(), exprString('formulas.*')),
  flow: z.object({ steps: z.array(flowStep) }).optional(),
  // G9 (ADR-026 Option B, schema 1.1.0): optional scope-key declaration. Shape here;
  // the meaning check (free-variable coverage) runs in crossCheckScopeContract below.
  scopeContract: z
    .object({
      scope: z.record(z.string(), z.string()),
      candidateFields: z.record(z.string(), z.array(z.string())).optional(),
    })
    .optional(),
  // Layer D (schema 1.2.0): optional "play" meta-model. B2 lands `initiative`; grows over B3/B4.
  play: playModel.optional(),
})

/**
 * G9 cross-check (ADR-026 Option B): when a pack declares `scopeContract`, every flow
 * predicate's free variables must be covered. Free variables partition into: the pack's own
 * `functions`/`derived` names, per-candidate fields declared for the choice (`eligible` only,
 * plus the always-injected `candidateId`), and host-scope keys — anything left over is an
 * undeclared scope key and throws with a precise path (same loud-fail as the Zod gate).
 */
function crossCheckScopeContract(pack: ContentPack): void {
  const contract = pack.scopeContract
  if (!contract) return
  const fail = (path: string, message: string): never => {
    throw new Error(`[engine] invalid content pack at "${path}": ${message}`)
  }
  const packNames = new Set([...Object.keys(pack.functions), ...Object.keys(pack.derived)])
  const scopeKeys = new Set(Object.keys(contract.scope))
  const steps = pack.flow?.steps ?? []
  const choiceIds = new Set(steps.flatMap((s) => s.choices.map((c) => c.id)))
  for (const declared of Object.keys(contract.candidateFields ?? {})) {
    if (!choiceIds.has(declared)) {
      fail(
        `scopeContract.candidateFields.${declared}`,
        `declares candidate fields for unknown choice "${declared}"`,
      )
    }
  }
  const freeVars = (expr: string): string[] => exprParser.parse(expr).variables()
  steps.forEach((step, si) => {
    for (const name of freeVars(step.valid)) {
      if (packNames.has(name) || scopeKeys.has(name)) continue
      fail(
        `flow.steps.${si}.valid`,
        `free variable "${name}" is not a declared scopeContract.scope key (step "${step.id}")`,
      )
    }
    step.choices.forEach((choice, ci) => {
      if (typeof choice.count === 'string' && !scopeKeys.has(choice.count)) {
        fail(
          `flow.steps.${si}.choices.${ci}.count`,
          `count variable "${choice.count}" is not a declared scopeContract.scope key (choice "${choice.id}")`,
        )
      }
      if (choice.eligible !== undefined) {
        const fields = new Set(contract.candidateFields?.[choice.id] ?? [])
        for (const name of freeVars(choice.eligible)) {
          if (
            name === 'candidateId' ||
            fields.has(name) ||
            scopeKeys.has(name) ||
            packNames.has(name)
          ) {
            continue
          }
          fail(
            `flow.steps.${si}.choices.${ci}.eligible`,
            `free variable "${name}" is neither a scopeContract.scope key nor a declared candidate field for choice "${choice.id}"`,
          )
        }
      }
    })
  })
}

/**
 * Layer D cross-check: meaning-checks the declared `play` refs that Zod (shape-only) cannot.
 *
 * §2.1 (ADR-031): a declared `play.resolution` rule must reference a `play.dice` model that exists, and
 * every `outcomes[].when` predicate may read only the scalars the rule declares in `scope`.
 * §2.2 (ADR-030): each `play.actions.downgrades[].from`/`.to` must reference a declared `kinds[].id`.
 * §2.7 (ADR-086): recovery ids are unique, and one recovery carries at most one `reset-usage` and one
 * `clear-effects` — two of a kind would make the plan's key set order-dependent.
 * An undeclared ref (dice / action kind) or a `when` free variable outside `scope` throws with a precise
 * path — the same loud-fail discipline as `crossCheckScopeContract`, so a rule the host can't honour fails
 * at engine creation.
 */
function crossCheckPlay(pack: ContentPack): void {
  const play = pack.play
  if (!play) return
  const fail = (path: string, message: string): never => {
    throw new Error(`[engine] invalid content pack at "${path}": ${message}`)
  }

  if (play.resolution) {
    const diceNames = new Set(Object.keys(play.dice ?? {}))
    for (const [ruleId, rule] of Object.entries(play.resolution)) {
      if (!diceNames.has(rule.dice)) {
        fail(
          `play.resolution.${ruleId}.dice`,
          `references unknown dice model "${rule.dice}" (declared: ${[...diceNames].join(', ') || 'none'})`,
        )
      }
      const scope = new Set(rule.scope)
      rule.outcomes.forEach((outcome, oi) => {
        for (const name of exprParser.parse(outcome.when).variables()) {
          if (!scope.has(name)) {
            fail(
              `play.resolution.${ruleId}.outcomes.${oi}.when`,
              `free variable "${name}" is not a declared scope scalar (scope: ${rule.scope.join(', ') || 'none'})`,
            )
          }
        }
      })
    }
  }

  // §2.2 — action-economy downgrade edges must reference declared kinds (open ids; ref-checked here).
  if (play.actions) {
    const kindIds = new Set(play.actions.kinds.map((k) => k.id))
    play.actions.downgrades?.forEach((edge, di) => {
      for (const side of ['from', 'to'] as const) {
        if (!kindIds.has(edge[side])) {
          fail(
            `play.actions.downgrades.${di}.${side}`,
            `references unknown action kind "${edge[side]}" (declared: ${[...kindIds].join(', ') || 'none'})`,
          )
        }
      }
    })
  }

  // §2.7 — recovery ids must be unique (the host looks one up by id) and a recovery may declare at
  // most one `reset-usage` / one `clear-effects` (the plan's key and survivor sets stay unambiguous).
  if (play.recovery) {
    const seenIds = new Set<string>()
    play.recovery.forEach((rec, ri) => {
      if (seenIds.has(rec.id)) fail(`play.recovery.${ri}.id`, `duplicate recovery id "${rec.id}"`)
      seenIds.add(rec.id)
      const firstOfKind = new Map<string, number>()
      rec.effects.forEach((effect, ei) => {
        if (effect.kind !== 'reset-usage' && effect.kind !== 'clear-effects') return
        if (firstOfKind.has(effect.kind)) {
          fail(`play.recovery.${ri}.effects.${ei}`, `more than one "${effect.kind}" effect`)
        }
        firstOfKind.set(effect.kind, ei)
      })
    })
  }
}

/**
 * Validate a content pack, throwing with a precise path to the first offending key on failure.
 * Absent optional keys default silently (Zod treats them as omitted); a *present* malformed key —
 * a bad expression, an unknown `source` kind — throws. Call at engine creation.
 */
export function validatePack(pack: ContentPack): void {
  const result = contentPackSchema.safeParse(pack)
  if (!result.success) {
    const issue = result.error.issues[0]!
    const path = issue.path.length ? issue.path.join('.') : '(root)'
    throw new Error(`[engine] invalid content pack at "${path}": ${issue.message}`)
  }
  crossCheckScopeContract(pack)
  crossCheckPlay(pack)
}
